import type { HTMLAttributes, JSX, ReactNode } from 'react';
import { useEffect, useId } from 'react';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

export interface DialogProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  size?: DialogSize;
  children: ReactNode;
}

const sizeStyles: Record<DialogSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

/**
 * Dialog component - a reusable modal dialog with backdrop and keyboard handling
 *
 * Features:
 * - Click-to-close backdrop
 * - Escape key handling
 * - Configurable sizes
 * - Proper accessibility attributes
 * - Optional title section
 */
export function Dialog({
  isOpen,
  onClose,
  title,
  size = 'md',
  className = '',
  children,
  ...props
}: DialogProps): JSX.Element | null {
  const titleId = useId();

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
    >
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/40 focus:outline-none"
        onClick={onClose}
        aria-label="Close dialog"
      />

      {/* Dialog content */}
      <div
        className={`relative bg-white rounded-xl shadow-lg w-full ${sizeStyles[size]} mx-4 p-6 ${className}`}
        {...props}
      >
        {title && (
          <h3 id={titleId} className="text-xl font-semibold mb-4">
            {title}
          </h3>
        )}
        {children}
      </div>
    </div>
  );
}
